import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Save } from 'lucide-react';
import { toast } from 'sonner';
import { useUtmMapSettings, useUpdateUtmMapSettings } from '@/hooks/utm';

const DEFAULT_VALUES = {
  longitude: -5.5471,
  latitude: 7.54,
  zoom: 6.5,
};

const toNumber = (value: string) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : NaN;
};

const UtmMapSettingsPage = () => {
  const { data, isLoading, isError, refetch } = useUtmMapSettings();
  const { mutateAsync: updateSettings, isPending } = useUpdateUtmMapSettings();

  const [longitude, setLongitude] = useState('');
  const [latitude, setLatitude] = useState('');
  const [zoom, setZoom] = useState('');

  useEffect(() => {
    const source = data || DEFAULT_VALUES;
    setLongitude(String(source.longitude));
    setLatitude(String(source.latitude));
    setZoom(String(source.zoom));
  }, [data]);

  const validationError = useMemo(() => {
    const lng = toNumber(longitude);
    const lat = toNumber(latitude);
    const zm = toNumber(zoom);

    if (!Number.isFinite(lng) || lng < -180 || lng > 180) {
      return 'Longitude must be between -180 and 180.';
    }

    if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
      return 'Latitude must be between -90 and 90.';
    }

    if (!Number.isFinite(zm) || zm < 0 || zm > 22) {
      return 'Zoom size must be between 0 and 22.';
    }

    return null;
  }, [longitude, latitude, zoom]);

  const handleSave = async (event: FormEvent) => {
    event.preventDefault();

    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      await updateSettings({
        longitude: toNumber(longitude),
        latitude: toNumber(latitude),
        zoom: toNumber(zoom),
      });
      toast.success('Map settings saved successfully.');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to save map settings.');
    }
  };

  return (
    <div className="mx-auto max-w-2xl p-4 sm:p-6 lg:p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Maps settings</h1>
        <p className="mt-1 text-muted-foreground">
          Set the default location and zoom level used in UzaMaps UTM views.
        </p>
      </div>

      {isError ? (
        <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-700">
          Failed to load map settings.
          <button
            type="button"
            onClick={() => refetch()}
            className="ml-3 underline underline-offset-2"
          >
            Retry
          </button>
        </div>
      ) : (
        <form onSubmit={handleSave} className="space-y-5 rounded-lg border border-border bg-card p-4 sm:p-6">
          <div>
            <label className="mb-2 block text-sm font-medium text-foreground">Longitude</label>
            <input
              type="number"
              step="any"
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
              placeholder="-5.5471"
              className="w-full rounded-lg border border-border bg-muted px-4 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              disabled={isLoading || isPending}
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-foreground">Latitude</label>
            <input
              type="number"
              step="any"
              value={latitude}
              onChange={(e) => setLatitude(e.target.value)}
              placeholder="7.54"
              className="w-full rounded-lg border border-border bg-muted px-4 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              disabled={isLoading || isPending}
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-foreground">Zoom size</label>
            <input
              type="number"
              step="any"
              value={zoom}
              onChange={(e) => setZoom(e.target.value)}
              placeholder="6.5"
              className="w-full rounded-lg border border-border bg-muted px-4 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              disabled={isLoading || isPending}
            />
          </div>

          {validationError && (
            <p className="text-sm text-red-600">{validationError}</p>
          )}

          <div className="pt-2">
            <button
              type="submit"
              disabled={Boolean(validationError) || isLoading || isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-muted"
            >
              <Save size={16} />
              {isPending ? 'Saving...' : 'Save settings'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default UtmMapSettingsPage;
